import { BrandingColor, BasePrice, PriceOpt } from "app/api/product/product.type";
import { UserType } from "app/api/user/user.types";
import { userAtom } from "app/atoms/user.atom";
import { colorStockDataI } from "app/components/Product/StockData";
import {
  calculatePercentage,
  formatToTwoDecimalPlaces,
  formatToTwoDecimalPlacesNumber,
} from "app/utils/fn";
import { useAtomValue } from "jotai";
import { useState, useEffect } from "react";

// Adjust product quantity based on minimum and stock level
function useAdjustedProductQty(
  productQty: number,
  minQuantity: number,
  selectedColor: colorStockDataI,
) {
  const [adjustedQty, setAdjustedQty] = useState(productQty);

  useEffect(() => {
    if (selectedColor?.qtyOnHand !== undefined && selectedColor.qtyOnHand !== 0) {
      if (selectedColor.qtyOnHand >= minQuantity) {
        setAdjustedQty((prev) => (prev < minQuantity ? minQuantity : prev));
      } else {
        setAdjustedQty(selectedColor.qtyOnHand);
      }
    }
  }, [adjustedQty, minQuantity, selectedColor?.qtyOnHand]);

  return [adjustedQty, setAdjustedQty] as const;
}

// Apply discount settings and trigger additional decoration selection if needed.
function useDiscountSettings(
  data: any,
  currentPrice: any,
  currentBrandingType: { name: string; friendlyName: string },
  decorationOptions: any,
  selectedArtColor: keyof BrandingColor,
  selectedBrandingPosition: string,
  handleAdditionalDecorationSelection: (key: string) => void,
  setMinMarkUp: React.Dispatch<React.SetStateAction<number>>,
  setProfitDiscount: React.Dispatch<React.SetStateAction<number>>,
) {
  const user = useAtomValue(userAtom);
  const slug = data?.product.slug; // Extract slug explicitly
  useEffect(() => {
    // const { user } = data || {};

    const { minimumProfitAmount, defaultProfitDiscount } = data.profitSettings || {};
    const userType = user?.userType || UserType.Non_VIP;

    if (currentPrice) {
      const discountSettings = [
        {
          type: UserType.Non_VIP,
          minMarkup: minimumProfitAmount?.nonVip,
          discount: defaultProfitDiscount?.nonVip,
        },
        {
          type: UserType.Gold_VIP,
          minMarkup: minimumProfitAmount?.goldVip,
          discount: defaultProfitDiscount?.goldVip,
        },
        {
          type: UserType.DIAMOND_VIP,
          minMarkup: minimumProfitAmount?.diamondVip,
          discount: defaultProfitDiscount?.diamondVip,
        },
      ];

      for (const { type, minMarkup, discount } of discountSettings) {
        if (currentBrandingType && decorationOptions[currentBrandingType.name]?.positions) {
          // const newAddition = findAdditionByDescription(
          //   selectedAddition ?? "",
          //   data?.product?.product?.prices?.priceGroups,
          // );

          if (selectedBrandingPosition)
            handleAdditionalDecorationSelection(selectedBrandingPosition);
        }
        if (userType === type) {
          setMinMarkUp(minMarkup ?? 0);

          setProfitDiscount(discount ?? 0);
          break;
        }
      }
    }
  }, [
    user?.userType,
    slug, // Explicitly include slug
    data,
    currentPrice,
    currentBrandingType,
    decorationOptions,
    selectedArtColor,
    selectedBrandingPosition,
    handleAdditionalDecorationSelection,
    setMinMarkUp,
    setProfitDiscount,
  ]);
}

/* ========= Helper Functions ========= */

/**
 * Computes product pricing based on base price, quantity tiers, and additions.
 * Returns both dynamic pricing (based on current quantity) and fixed pricing (based on minimum quantity).
 *
 * @param basePrice - Base price with quantity-based price breaks
 * @param currentProductQty - User's selected quantity
 * @param additionsPriceBrakes - Price breaks for additions (e.g. decoration)
 * @param minMarkup - Minimum profit amount (floor for markup)
 * @param profitDiscount - Percentage applied to subtotal for markup calculation
 * @param supplierIsSpecial - If true, uses lowest price break; if false, uses quantity-tiered pricing
 * @param productMinQuantity - Minimum order quantity (used for fixed pricing)
 */
const computeProductPrice = (
  basePrice: BasePrice,
  _currentProductQty: number,
  additionsPriceBrakes: PriceOpt | undefined,
  minMarkup: number,
  profitDiscount: number,
  supplierIsSpecial: boolean,
  productMinQuantity: number,
) => {
  const currentProductQty =
    _currentProductQty > productMinQuantity ? _currentProductQty : productMinQuantity;
  // --- Initial price break (entry-level tier) ---
  // Special suppliers: always use the lowest price across all breaks
  // Regular suppliers: use the break that matches or exceeds productMinQuantity
  const initialPriceBreak = supplierIsSpecial
    ? basePrice?.priceBreaks.reduce(
        (min, current) => (current.price < min.price ? current : min),
        basePrice?.priceBreaks[0],
      )
    : basePrice?.priceBreaks.find((breakItem) => productMinQuantity <= breakItem.qty) ||
      basePrice?.priceBreaks[basePrice?.priceBreaks.length - 1];

  const initialBasePrice = initialPriceBreak?.price || 0;

  // --- Base price for current quantity ---
  // Find the highest price break that currentProductQty qualifies for (higher qty = lower unit price)
  const basePriceBreak =
    basePrice?.priceBreaks
      ?.sort((a, b) => a.qty - b.qty)
      .filter((breakItem) => currentProductQty >= breakItem.qty)
      .pop() || basePrice?.priceBreaks[0];

  // Special suppliers keep the initial (lowest) price; others use the quantity-tiered price
  const basePriceValue = supplierIsSpecial
    ? initialBasePrice
    : basePriceBreak?.price || initialBasePrice;

  // --- Additions price (decoration, etc.) ---
  // Find the addition price break for current quantity (first break where breakItem.qty >= currentProductQty)
  const sortedAdditionBreaks =
    additionsPriceBrakes?.priceBreaks?.sort((a, b) => a.qty - b.qty) || [];

  const additionPriceBreak =
    sortedAdditionBreaks.find((breakItem) => currentProductQty <= breakItem.qty) ||
    sortedAdditionBreaks[sortedAdditionBreaks.length - 1];

  const additionsPrice = additionPriceBreak?.price || 0;

  // Fixed pricing uses the highest addition tier (last break) and initial base price
  const fixedAdditionsPrice = sortedAdditionBreaks[sortedAdditionBreaks.length - 1]?.price || 0;
  const fixedBasePriceValue = initialBasePrice;

  // ---------- DYNAMIC PRICE (based on current quantity) ----------
  const subtotal = (basePriceValue + additionsPrice) * currentProductQty;
  const dynamicMarkup = Math.max(minMarkup, calculatePercentage(subtotal, profitDiscount));
  const dynamicUnitPrice = formatToTwoDecimalPlacesNumber(
    (subtotal + dynamicMarkup) / currentProductQty,
  );
  const dynamicGrandTotal = formatToTwoDecimalPlacesNumber(dynamicUnitPrice * currentProductQty);

  // ---------- FIXED PRICE (based on minimum quantity) ----------
  const fixedSubtotal = (fixedBasePriceValue + fixedAdditionsPrice) * productMinQuantity;
  const fixedMarkup = Math.max(minMarkup, calculatePercentage(fixedSubtotal, profitDiscount));
  const fixedUnitPrice = formatToTwoDecimalPlacesNumber(
    (fixedSubtotal + fixedMarkup) / productMinQuantity,
  );
  const fixedGrandTotal = formatToTwoDecimalPlacesNumber(fixedUnitPrice * productMinQuantity);

  return {
    initialBasePrice,
    basePrice: Math.floor(dynamicUnitPrice * 100) / 100,
    totalAdditionsPrice: additionsPrice + basePriceValue,
    actualUnitPrice: dynamicUnitPrice,
    grandTotal: dynamicGrandTotal,

    // Fixed pricing (minimum-quantity baseline)
    fixedUnitPrice,
    fixedGrandTotal,
    fixedTotalAdditionsPrice: fixedAdditionsPrice + fixedBasePriceValue,
  };
};

export { useAdjustedProductQty, useDiscountSettings, computeProductPrice };